/**
 * InventoryManager - Presenter Component
 *
 * Pure presenter that renders the inventory editing UI using EditableListManager.
 * Receives the global items list from InventoryManagerContainer and the
 * inventory items + onChange handler from the parent (CharacterSheetTabs).
 * Contains ZERO store access.
 *
 * @see docs/adr/0034-container-presenter-component-pattern.md
 */

import React from 'react';
import { AutoComplete, InputNumber } from 'antd';
import type { PartyInventory, Item } from '../../types/types';
import EditableListManager from './EditableListManager';
import styles from './EditableListManager.module.css';

/**
 * Props passed through from the parent (CharacterSheetTabs) that are not
 * sourced from the store by InventoryManagerContainer.
 */
export interface InventoryManagerPassthroughProps {
  items: PartyInventory[];
  onChange: (newItems: PartyInventory[] | ((prevItems: PartyInventory[]) => PartyInventory[])) => void;
}

/**
 * Full props interface including data from the Container.
 */
export interface InventoryManagerProps extends InventoryManagerPassthroughProps {
  allItems: Item[];
}

const InventoryManager: React.FC<InventoryManagerProps> = ({ items, onChange, allItems }) => {
  const itemOptions = (allItems ?? []).map(item => ({
    value: item.name,
    label: item.name,
    item, // Attach the full item object
  }));

  return (
    <EditableListManager<PartyInventory>
      items={items}
      onChange={onChange}
      defaultNewItem={() => ({
        quantity: 1,
        partyMemberId: 0, // This will be set correctly on save
        itemId: 0, // This will be set correctly on save
        updatedAt: new Date(),
        item: { id: 0, name: '' } as Item,
      })}
      addText="Add Item"
      renderItemFields={(item, index, handleItemChange) => (
        <div className={styles.itemFields}>
          <AutoComplete
            placeholder="Item Name"
            value={item.item.name}
            options={itemOptions}
            style={{ flexGrow: 1 }}
            onSelect={(_value, option) => {
              // When an item is selected from the dropdown, update the whole item object
              handleItemChange(index, { item: option.item, itemId: option.item.id });
            }}
            onChange={(value) => {
              // As the user types, just update the name for now
              handleItemChange(index, { item: { ...item.item, name: value } });
            }}
            filterOption={(inputValue, option) =>
              option!.value.toUpperCase().indexOf(inputValue.toUpperCase()) !== -1
            }
          />
          <InputNumber
            min={1}
            placeholder="Qty"
            style={{ width: '80px' }}
            value={item.quantity}
            onChange={(value) => handleItemChange(index, { quantity: value ?? 1 })}
          />
        </div>
      )}
    />
  );
};

export default InventoryManager;
